/**
 * 新建岗位 Wizard:3 步 + 深度分析报告
 *   ① 招聘需求(基础 4 字段 + 招聘简章原文 + 老板原话 提升输出质量)
 *   ② AI 深度分析报告(8 段折叠卡片) + 能力清单勾选/编辑
 *   ③ 确认 + 创建
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyzeRequirements, createJob } from '../api/interview';
import type { AnalysisReport, Capability } from '../api/types';
import { ReportView } from '../components/ReportView';
import { ErrorBoundary } from '../components/ErrorBoundary';

export default function AdminJobNew() {
  const nav = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // step 1
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [charterFull, setCharterFull] = useState('');
  const [bossVoice, setBossVoice] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // step 2 —— LLM 输出
  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [report, setReport] = useState<AnalysisReport | null>(null);
  const [customCap, setCustomCap] = useState('');

  const handleAnalyze = async () => {
    if (!title.trim() || !description.trim()) { setError('岗位名称和描述都要填'); return; }
    try {
      setBusy(true); setError(null);
      const r = await analyzeRequirements({
        title, description,
        charter_full: charterFull.trim() || undefined,
        boss_voice: bossVoice.trim() || undefined,
      });
      setCapabilities(r.capabilities.map((c) => ({ ...c, selected: true, custom: false })));
      setReport(r.analysis_report);
      setStep(2);
    } catch (e: any) { setError(e.message); }
    finally { setBusy(false); }
  };

  const toggleCap = (i: number) => {
    setCapabilities((arr) => arr.map((c, idx) => idx === i ? { ...c, selected: !c.selected } : c));
  };
  const removeCap = (i: number) => {
    setCapabilities((arr) => arr.filter((_, idx) => idx !== i));
  };
  const addCustomCap = () => {
    const name = customCap.trim();
    if (!name) return;
    setCapabilities((arr) => [...arr, { name, priority: 'must_have', description: '', selected: true, custom: true }]);
    setCustomCap('');
  };

  const handleCreate = async () => {
    try {
      setBusy(true); setError(null);
      const job = await createJob({
        title, description,
        capabilities,
        analysis_report: report ?? undefined,
      });
      nav(`/admin/job/${job.id}`);
    } catch (e: any) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="container narrow">
      <div className="page-header">
        <h1 className="title">新建岗位</h1>
      </div>

      {error && <div className="banner error">{error}</div>}

      {/* 步骤指示器 */}
      <div className="steps">
        <div className={`step ${step === 1 ? 'active' : step > 1 ? 'done' : ''}`}>
          <div className="num">{step > 1 ? '✓' : '1'}</div>
          <div className="text"><div className="title">招聘需求</div><div className="hint">基础 + 招聘简章 + 老板原话</div></div>
        </div>
        <div className={`step ${step === 2 ? 'active' : step > 2 ? 'done' : ''}`}>
          <div className="num">{step > 2 ? '✓' : '2'}</div>
          <div className="text"><div className="title">AI 深度分析</div><div className="hint">画像 + 能力 + 招人坑</div></div>
        </div>
        <div className={`step ${step === 3 ? 'active' : ''}`}>
          <div className="num">3</div>
          <div className="text"><div className="title">确认</div><div className="hint">检查并创建</div></div>
        </div>
      </div>

      {/* ──────── Step 1 ──────── */}
      {step === 1 && (
        <div className="card">
          <h2>招聘需求</h2>
          <div className="col">
            <label>
              <div className="hint" style={{ marginBottom: 4 }}>岗位名称 *</div>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="如:AI 业务架构师 / 抖音电商主播 / 销售总监" />
            </label>
            <label>
              <div className="hint" style={{ marginBottom: 4 }}>岗位描述 *</div>
              <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder="一句话描述岗位核心任务,如:用 AI 重构线束制造工厂全业务流,核心攻坚:报价从天级到小时级" />
            </label>

            {/* 进阶输入(强烈推荐) */}
            <div style={{
              padding: 14, marginTop: 8,
              background: 'var(--accent-soft)',
              borderLeft: '3px solid var(--accent)',
              borderRadius: 8,
            }}>
              <div className="row" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>📈 进阶输入(强烈推荐,提升输出质量 2-3 倍)</div>
                <button className="ghost icon" onClick={() => setShowAdvanced(!showAdvanced)}>
                  {showAdvanced ? '收起' : '展开'}
                </button>
              </div>
              {!showAdvanced && (
                <div className="hint" style={{ fontSize: 12 }}>
                  贴入完整招聘简章 + 老板/HR 对岗位说过的原话,AI 可以输出"招人坑提示"+"反向 challenge"+"战略级岗位定位"
                </div>
              )}
              {showAdvanced && (
                <div className="col" style={{ gap: 10, marginTop: 8 }}>
                  <label>
                    <div className="hint" style={{ marginBottom: 4 }}>招聘简章原文(可选,直接贴入完整 JD)</div>
                    <textarea rows={5} value={charterFull} onChange={(e) => setCharterFull(e.target.value)}
                      placeholder="如:岗位核心工作 1. 诊断现有业务流程...&#10;任职要求:精通 X / 熟悉 Y...&#10;专属优势:无 X 内耗 / 无 Y 壁垒..." />
                  </label>
                  <label>
                    <div className="hint" style={{ marginBottom: 4 }}>老板/HR 对这岗位说过的话(可选,关键观点逐条列出)</div>
                    <textarea rows={6} value={bossVoice} onChange={(e) => setBossVoice(e.target.value)}
                      placeholder="如:&#10;1. 我们要的不是 AI 辅助人,而是 AI 主导人配合...&#10;2. 报价是切入点,后面是把整个业务流重构...&#10;3. 虽然小智 1.0 不够智能,但已经突破了人力维度..." />
                  </label>
                </div>
              )}
            </div>

            <div className="hint">
              下一步用 4 阶段 LLM 流水线生成深度分析报告(画像 / 能力 / 招人坑 / 反向 challenge),约 2-3 分钟
            </div>

            <div className="row" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={handleAnalyze} disabled={busy}>
                {busy ? 'AI 深度分析中...(约 2-3 分钟)' : 'AI 深度分析 →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ──────── Step 2: 深度报告 + 能力清单 ──────── */}
      {step === 2 && (
        <>
          {/* 深度报告(可折叠卡片) */}
          {report && (
            <ErrorBoundary label="岗位深度分析报告">
              <ReportView report={report} />
            </ErrorBoundary>
          )}

          {/* 能力清单 — 按 6 维分组 */}
          <div className="card">
            <h2>面试考核能力 <span className="hint" style={{ fontSize: 13, fontWeight: 400 }}>· {capabilities.filter((c) => c.selected).length} / {capabilities.length} 已勾选 · 按上方 6 维能力模型分组</span></h2>
            <div className="hint" style={{ marginBottom: 12 }}>勾选要在面试中考核的具体能力 · 每条挂在它所属的能力维度下 · 可删除/添加自定义</div>

            {/* 按 dimension 分组渲染 —— 每条 capability 自带 dimension 标签 */}
            <div className="col" style={{ gap: 14 }}>
              {groupByDimension(capabilities).map(([dimName, items]) => (
                <div key={dimName || '__no_dim__'} style={{
                  padding: 12,
                  background: 'var(--bg-elev)',
                  borderRadius: 8,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', marginBottom: 8 }}>
                    📐 {dimName || '其他(自定义)'} ({items.length} 条)
                  </div>
                  <div className="col" style={{ gap: 6 }}>
                    {items.map(({ cap, idx }) => (
                      <div key={idx} style={{
                        padding: '10px 12px',
                        background: cap.selected ? 'var(--surface)' : 'transparent',
                        border: cap.selected ? '2px solid var(--accent)' : '1px dashed var(--border)',
                        borderRadius: 6,
                        opacity: cap.selected ? 1 : 0.5,
                      }}>
                        <div className="row" style={{ alignItems: 'flex-start', gap: 10 }}>
                          <input type="checkbox" checked={cap.selected} onChange={() => toggleCap(idx)}
                            style={{ width: 'auto', marginTop: 4 }} />
                          <div style={{ flex: 1 }}>
                            <div className="row" style={{ gap: 8 }}>
                              <span style={{ fontWeight: 500, fontSize: 13 }}>{cap.name}</span>
                              <span className="tag" style={{
                                background: cap.priority === 'must_have' ? 'var(--danger-soft)' : 'var(--surface-2)',
                                color: cap.priority === 'must_have' ? '#b91c1c' : 'var(--text-muted)',
                                fontSize: 11,
                              }}>
                                {cap.priority === 'must_have' ? '★ 必备' : '加分'}
                              </span>
                              {cap.custom && <span className="tag" style={{ fontSize: 11 }}>自定义</span>}
                            </div>
                            {cap.description && <div className="hint" style={{ marginTop: 4, fontSize: 12 }}>{cap.description}</div>}
                          </div>
                          <button className="ghost icon" onClick={() => removeCap(idx)} style={{ color: 'var(--danger)' }}>×</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="row" style={{ marginTop: 12, gap: 6 }}>
              <input value={customCap} onChange={(e) => setCustomCap(e.target.value)}
                placeholder="添加自定义能力(如:金融行业经验)"
                onKeyDown={(e) => e.key === 'Enter' && addCustomCap()} />
              <button className="secondary" onClick={addCustomCap} disabled={!customCap.trim()}>+ 添加</button>
            </div>

            <div className="row" style={{ justifyContent: 'space-between', marginTop: 24 }}>
              <button className="ghost" onClick={() => setStep(1)}>← 上一步</button>
              <button onClick={() => setStep(3)} disabled={capabilities.filter((c) => c.selected).length === 0}>
                下一步 →
              </button>
            </div>
          </div>
        </>
      )}

      {/* ──────── Step 3: 确认 ──────── */}
      {step === 3 && (
        <div className="card">
          <h2>确认信息</h2>
          <div className="col" style={{ gap: 16 }}>
            <div className="card flat" style={{ margin: 0 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>{title}</div>
              <div className="hint" style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{description}</div>
              <div className="row" style={{ marginTop: 8, gap: 6, flexWrap: 'wrap' }}>
                {report && <span className="chip" style={{ background: 'var(--success-soft)', color: '#047857' }}>📊 深度报告已生成</span>}
              </div>
            </div>

            <div>
              <div className="hint" style={{ marginBottom: 6 }}>
                考核能力 ({capabilities.filter((c) => c.selected).length} 条)
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.8 }}>
                {capabilities.filter((c) => c.selected).map((c, i) => (
                  <span key={i} style={{ display: 'inline-block', marginRight: 8, marginBottom: 4 }}>
                    <span style={{
                      padding: '2px 8px',
                      background: c.priority === 'must_have' ? 'var(--danger-soft)' : 'var(--surface-2)',
                      color: c.priority === 'must_have' ? '#b91c1c' : 'var(--text)',
                      borderRadius: 4,
                      fontSize: 12,
                    }}>
                      {c.priority === 'must_have' ? '★ ' : ''}{c.name}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="row" style={{ justifyContent: 'space-between', marginTop: 24 }}>
            <button className="ghost" onClick={() => setStep(2)}>← 上一步</button>
            <button onClick={handleCreate} disabled={busy}>
              {busy ? '创建中...' : '✓ 创建岗位'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * 把 capabilities 按 dimension 分组,保留原来的索引(给 toggleCap/removeCap 用)。
 * 返回 [['维度名', [{ cap, idx }, ...]], ...] 顺序保留首次出现顺序。
 * dimension 为 undefined / null / 空 的归到末尾"其他(自定义)"组。
 */
function groupByDimension(caps: Capability[]): [string, { cap: Capability; idx: number }[]][] {
  const map = new Map<string, { cap: Capability; idx: number }[]>();
  caps.forEach((cap, idx) => {
    const key = cap.dimension || '';
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push({ cap, idx });
  });
  // 把空 key 排到最后(自定义能力)
  const groups = Array.from(map.entries());
  return groups.sort((a, b) => (a[0] === '' ? 1 : 0) - (b[0] === '' ? 1 : 0));
}
